import { DirectoryData, FSNode, GITHUB_URL_PREFIX } from '@lved/shared-core';
import type { GitHubFetchOptions } from '../types/githubFetchOptions.js';
// Unauthenticated calls limited to 60 requests/hour per IP
import { GitHubService, ParsedRepo } from './GitHubService.js';
// Authenticated calls limited to 5000 requests/hour through AppWrite Functions proxies
import { AppwriteGitHubService } from './AppwriteGitHubService.js';
import { RepositoryArchiveSnapshot } from './RepositoryArchiveSnapshot.js';

// Will try to use the Appwrite GitHub proxy if the user is authenticated.
const USE_APPWRITE_GITHUB_SERVICE: boolean = true;

/**
 * GitHubFileSystem is a singleton service that abstracts away the details of interacting with GitHub repositories.
 * It handles both authenticated and unauthenticated scenarios, allowing for seamless access to repository contents.
 * The service determines which underlying implementation to use (GitHubService or AppwriteGitHubService) based on the user's authentication status and repository visibility.
 */
export class GitHubFileSystem {
    private static instance: GitHubFileSystem;
    public owner: string = '';
    public repo: string = '';
    public branch: string = 'main';
    public basePath: string = '';
    public repoParam: string = '';
    private isPublicRepo: boolean = false;
    private archiveSnapshot: RepositoryArchiveSnapshot | null = null;

    private static authCb: (() => boolean) | null = null;

    /**
     * Get the singleton instance of the GitHub file system
     */
    static getInstance(): GitHubFileSystem {
        if (!GitHubFileSystem.instance) GitHubFileSystem.instance = new GitHubFileSystem();
        return GitHubFileSystem.instance;
    }

    /**
     *  This prevents a dependency on the GitHubAuthStore, which has to be done before calling 'initialize'
     */
    static setAuthCallback(callback: () => boolean): void {
        GitHubFileSystem.authCb = callback;
    }

    /**
     * Check if user is authenticated with GitHub by calling the provided authentication callback.
     */
    private static isAuthenticated(): boolean {
        const authCb = GitHubFileSystem.authCb;
        return authCb !== null && authCb();
    }

    /**
     * Check if the archive snapshot is loaded
     */
    isArchiveSnapshotLoaded(): boolean {
        return this.archiveSnapshot !== null;
    }

    /**
     * Get the commit SHA of the current branch
     */
    async getBranchCommitSha(): Promise<string> {
        if (USE_APPWRITE_GITHUB_SERVICE && GitHubFileSystem.isAuthenticated()) {
            return AppwriteGitHubService.getBranchSha(this.owner, this.repo, this.branch);
        }
        return GitHubService.getBranchSha(this.owner, this.repo, this.branch);
    }

    /**
     * Check if a branch exists in the repository
     */
    private async branchExists(branch: string): Promise<boolean> {
        if (USE_APPWRITE_GITHUB_SERVICE && GitHubFileSystem.isAuthenticated()) {
            return AppwriteGitHubService.branchExists(this.owner, this.repo, branch);
        }

        return GitHubService.branchExists(this.owner, this.repo, branch);
    }

    /**
     * Resolve branch and basePath from 'tree' URL parameter if present (overrides branch and path)
     */
    private async resolveTreeBranch(repoInfo: ParsedRepo): Promise<void> {
        if (!repoInfo.repoParam) return;

        const repoParam: string = repoInfo.repoParam.replace(GITHUB_URL_PREFIX, '');
        const parts: string[] = repoParam.split('/');
        const treeIndex: number = parts.indexOf('tree');
        if (treeIndex === -1) return;

        const branchAndPathSegments: string[] = parts.slice(treeIndex + 1);
        if (branchAndPathSegments.length <= 1) return;

        // Try to find the correct branch by checking progressively shorter prefixes
        // For example, if branchAndPathSegments is ['feature', 'subdir', 'file'], it will check:
        // - 'feature/subdir/file' (unlikely branch name)
        // - 'feature/subdir' (possible branch name)
        // - 'feature' (most likely branch name)
        for (let i = branchAndPathSegments.length; i >= 1; i -= 1) {
            const candidateBranch: string = branchAndPathSegments.slice(0, i).join('/');
            const candidatePath: string = branchAndPathSegments.slice(i).join('/');
            const branchExists: boolean = await this.branchExists(candidateBranch);
            if (branchExists) {
                this.branch = candidateBranch;
                this.basePath = candidatePath;
                return;
            }
        }
    }

    /**
     * Initialize the GitHub file system with repository details
     */
    async initialize(repoInfoOverride?: ParsedRepo): Promise<void> {
        const repoInfo: ParsedRepo | null = repoInfoOverride ?? GitHubService.parseCleanRepoFromUrl();
        if (!repoInfo) throw new Error('No repository information found in URL parameters');
        if (!repoInfo.owner || !repoInfo.repo) throw new Error('Missing GitHub repository owner or name.');

        console.debug('[GitHubFileSystem] Parsed repo info from URL', repoInfo);

        const initializationStartedAt: number = Date.now();
        const branchResolutionStartedAt: number = Date.now();
        this.isPublicRepo = await GitHubService.isPublicRepository(repoInfo.owner, repoInfo.repo);

        if (USE_APPWRITE_GITHUB_SERVICE && GitHubFileSystem.isAuthenticated()) {
            console.debug('Requesting GitHub token...');
            await AppwriteGitHubService.getGithubToken();
        }

        this.owner = repoInfo.owner;
        this.repo = repoInfo.repo;
        this.basePath = repoInfo.path || '';
        this.branch = repoInfo.branch || (await GitHubService.detectDefaultBranch(this.owner, this.repo));
        this.repoParam = repoInfo.repoParam || '';

        await this.resolveTreeBranch(repoInfo);
        const branchResolutionDurationSeconds: number = (Date.now() - branchResolutionStartedAt) / 1000;
        console.debug(
            `[GitHubFileSystem] Branch/default-branch resolution finished in ${branchResolutionDurationSeconds.toFixed(2)} seconds.`
        );

        this.archiveSnapshot = null;
        await this.tryLoadArchiveSnapshot();

        console.debug('[GitHubFileSystem] Initialized', {
            owner: this.owner,
            repo: this.repo,
            basePath: this.basePath,
            branch: this.branch,
            repoParam: this.repoParam,
            isPublicRepo: this.isPublicRepo,
            archiveSnapshotLoaded: this.isArchiveSnapshotLoaded()
        });
        const initializationDurationSeconds: number = (Date.now() - initializationStartedAt) / 1000;
        console.debug(
            `[GitHubFileSystem] Total initialization finished in ${initializationDurationSeconds.toFixed(2)} seconds.`
        );
    }

    private async tryLoadArchiveSnapshot(): Promise<void> {
        const snapshotStartedAt: number = Date.now();
        try {
            this.archiveSnapshot = await RepositoryArchiveSnapshot.load({
                owner: this.owner,
                repo: this.repo,
                ref: this.branch,
                privateRepository: !this.isPublicRepo
            });
            this.archiveSnapshot.logMetrics('loaded');
            const snapshotDurationSeconds: number = (Date.now() - snapshotStartedAt) / 1000;
            console.debug(
                `[GitHubFileSystem] ZIP repository snapshot loaded in ${snapshotDurationSeconds.toFixed(2)} seconds.`
            );
        } catch (error: unknown) {
            this.archiveSnapshot = null;
            console.warn(
                `[GitHubFileSystem] ZIP repository snapshot unavailable; falling back to API mode: ${String(error)}`
            );
        }
    }

    /**
     * Get directory data from GitHub repository
     */
    async getDirectoryData(dirPath: string): Promise<DirectoryData | null> {
        try {
            if (this.archiveSnapshot) {
                return {
                    files: this.archiveSnapshot.createManifest(dirPath).children,
                    dirPath
                };
            }

            let files: FSNode[];

            if (USE_APPWRITE_GITHUB_SERVICE && GitHubFileSystem.isAuthenticated()) {
                console.debug('Using AppwriteGitHubService (authenticated user)');
                files = await AppwriteGitHubService.fetchRepositoryContents(
                    this.owner,
                    this.repo,
                    dirPath,
                    this.branch
                );
            } else {
                console.debug('Using GitHubService (unauthenticated user)');
                files = await GitHubService.fetchRepositoryContents(this.owner, this.repo, dirPath, this.branch);
            }

            return {
                files,
                dirPath
            };
        } catch (error: unknown) {
            console.error(`Error getting directory data: ${String(error)}`);
            return null;
        }
    }

    /**
     * Get file content from GitHub repository.
     * Allow for overriding the configured branch for a specific one.
     */
    async getFileData(filePath: string, branch?: string, options?: GitHubFetchOptions): Promise<string> {
        try {
            console.debug('[GitHubFileSystem] getFileData request', {
                owner: this.owner,
                repo: this.repo,
                configuredBranch: this.branch,
                requestedBranch: branch,
                filePath,
                options,
                isPublicRepo: this.isPublicRepo,
                isAuthenticated: GitHubFileSystem.isAuthenticated()
            });
            if (this.archiveSnapshot && (!branch || branch === this.branch)) {
                const snapshotContent: string | null = this.archiveSnapshot.getFileData(filePath);
                if (snapshotContent !== null) return snapshotContent;
                this.archiveSnapshot.logMetrics('text cache miss');
            }

            if (this.isPublicRepo) {
                console.debug(`Get File Data: (${filePath}), Using GitHubService (public repo raw URL)`);
                return GitHubService.fetchFileContent(this.owner, this.repo, filePath, branch || this.branch, options);
            }
            if (USE_APPWRITE_GITHUB_SERVICE && GitHubFileSystem.isAuthenticated()) {
                console.debug(`Get File Data: (${filePath}), Using AppwriteGitHubService (authenticated user)`);
                return AppwriteGitHubService.fetchFileContent(this.owner, this.repo, filePath, branch || this.branch);
            } else {
                console.debug('Using GitHubService (unauthenticated user)');
                return GitHubService.fetchFileContent(this.owner, this.repo, filePath, branch || this.branch, options);
            }
        } catch (error: unknown) {
            const logMethod: typeof console.warn | typeof console.error = options?.suppressExpectedErrorLog
                ? console.warn
                : console.error;
            logMethod(`Error getting file data (${filePath}): ${String(error)}`);
            throw error;
        }
    }

    /**
     * Get binary file data from GitHub repository
     */
    async getBinaryFileData(filePath: string, branch?: string, options?: GitHubFetchOptions): Promise<Uint8Array> {
        try {
            console.debug('[GitHubFileSystem] getBinaryFileData request', {
                owner: this.owner,
                repo: this.repo,
                configuredBranch: this.branch,
                requestedBranch: branch,
                filePath,
                options,
                isPublicRepo: this.isPublicRepo,
                isAuthenticated: GitHubFileSystem.isAuthenticated()
            });
            if (this.archiveSnapshot && (!branch || branch === this.branch)) {
                const snapshotContent: Uint8Array | null = this.archiveSnapshot.getBinaryFileData(filePath);
                if (snapshotContent !== null) return snapshotContent;
                this.archiveSnapshot.logMetrics('binary cache miss');
            }

            if (this.isPublicRepo) {
                console.debug(`Get Binary File Data: (${filePath}), Using GitHubService (public repo raw URL)`);
                return GitHubService.fetchBinaryFileContent(
                    this.owner,
                    this.repo,
                    filePath,
                    branch || this.branch,
                    options
                );
            }
            if (USE_APPWRITE_GITHUB_SERVICE && GitHubFileSystem.isAuthenticated()) {
                console.debug(`Get Binary File Data: (${filePath}), Using AppwriteGitHubService (authenticated user)`);
                return AppwriteGitHubService.fetchBinaryFileContent(
                    this.owner,
                    this.repo,
                    filePath,
                    branch || this.branch
                );
            } else {
                console.debug('Using GitHubService (unauthenticated user)');
                return GitHubService.fetchBinaryFileContent(
                    this.owner,
                    this.repo,
                    filePath,
                    branch || this.branch,
                    options
                );
            }
        } catch (error: unknown) {
            const logMethod: typeof console.warn | typeof console.error = options?.suppressExpectedErrorLog
                ? console.warn
                : console.error;
            logMethod(`Error getting binary file data: ${String(error)}`);
            throw error;
        }
    }

    /**
     * Create manifest data from GitHub repository
     */
    async createManifestData(): Promise<{ path: string; children: FSNode[] }> {
        if (this.archiveSnapshot) {
            const manifestStartedAt: number = Date.now();
            const manifestData: { path: string; children: FSNode[] } = this.archiveSnapshot.createManifest(
                this.basePath
            );
            const manifestDurationSeconds: number = (Date.now() - manifestStartedAt) / 1000;
            console.debug(
                `[GitHubFileSystem] ZIP manifest creation finished in ${manifestDurationSeconds.toFixed(2)} seconds.`
            );
            return manifestData;
        }

        if (USE_APPWRITE_GITHUB_SERVICE && GitHubFileSystem.isAuthenticated()) {
            console.debug('Using AppwriteGitHubService (authenticated user)');
            return await AppwriteGitHubService.createManifestFromRepo(
                this.owner,
                this.repo,
                this.basePath,
                this.branch
            );
        } else {
            console.debug('Using GitHubService (unauthenticated user)');
            // Use the static method from GitHubService
            return await GitHubService.createManifestFromRepo(this.owner, this.repo, this.basePath, this.branch);
        }
    }
}
